import { Component, SimpleChanges, inject, output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectEvent, RemoveEvent, KENDO_FILESELECT } from '@progress/kendo-angular-upload';
import { FileDto } from 'src/app/common/FileDto';
import { faDownload, faTrashAlt, faFile } from '@fortawesome/free-solid-svg-icons';
import { FileService } from 'src/app/services/file.service';
import { FilePostDto } from '../../common/FilePostDto';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
    imports: [CommonModule, KENDO_FILESELECT, FontAwesomeModule],
    selector: 'app-file-select',
    templateUrl: './file-select.component.html',
    styleUrls: ['./file-select.component.css']
})
export class FilesComponent {
  private fileService = inject(FileService);

  readonly Files = input<FilePostDto[]>();
  readonly showDownload = input<boolean>(false);
  readonly fileAdded = output<FilePostDto>();
  readonly fileRemoved = output<FilePostDto>();
  readonly fileDownload = output<FilePostDto>();

  public faTrashAlt = faTrashAlt;
  public faDownload = faDownload;
  public faFile = faFile;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['Files']?.currentValue) {
      // Legacy fallback: only files that came hydrated with base64 but without
      // server thumbnail URLs still need a data-URL built for the preview.
      this.Files()
        .filter((f) => this.isImage(f.filePayload))
        .filter((f) => !f.filePayload.image && !f.filePayload.imageUrls?.length && f.filePayload.base64Data)
        .forEach((f) => this.fileService.createImageFromBase64(f.filePayload));
    }
  }

  public isImage(file: FileDto | null): boolean {
    if (!file) return false;
    if (file.image || file.imageUrls?.length) return true;
    const name = file.originalFileName?.toLowerCase() ?? '';
    return /\.(jpe?g|png|gif|bmp|webp|svg)$/.test(name);
  }

  /** Preview source: local data-URL for newly picked files, server thumbnail for stored ones. */
  public previewSrc(file: FileDto | null): string | null {
    if (!file) return null;
    return file.image ?? file.imageUrls?.[0]?.smallImageUrl ?? null;
  }

  public selectImageEventHandler(e: SelectEvent) {
    let fileDto: FileDto = {
      base64Data: '',
      title: e.files[0].name,
      originalFileName: e.files[0].name,
      image: null,
      uid: e.files[0].uid,
      loadedFromFile: true,
      imageUrls: [],
      id: null,
      rawFile: e.files[0].rawFile,
    };
    let filePostDto: FilePostDto = {
      filePayload: fileDto,
      isCreated: true,
      isDeleted: false,
    };
    this.Files().push(filePostDto);
    if (this.isImage(fileDto)) {
      this.fileService.createImageFromFile(e.files[0].rawFile, fileDto);
    }
    this.fileAdded.emit(filePostDto);
  }

  public removeImageEventHandler($event: RemoveEvent) {
    const indexToRemove = this.Files().findIndex((f) => f.filePayload.uid == $event.files[0].uid);
    const removed = this.Files()[indexToRemove];
    if (indexToRemove != -1) {
      this.setDeleted(removed);
    }
  }

  public removeFile(filePostDto: FilePostDto) {
    var indexToRemove = this.getFileIndex(filePostDto);
    if (indexToRemove != -1) {
      const removed = this.Files()[indexToRemove];
      this.setDeleted(removed);
      this.fileRemoved.emit(removed);
    }
  }

  public downloadFile(filePostDto: FilePostDto) {
    this.fileDownload.emit(filePostDto);
  }

  private setDeleted(removedDto: FilePostDto) {
    removedDto.isDeleted = true;
    removedDto.isCreated = false;
  }

  private getFileIndex(filePostDto: FilePostDto): number {
    const indexToRemove = this.Files().findIndex(
      (f) => {
        if (filePostDto.filePayload.id) {
          return filePostDto.filePayload.id == f.filePayload.id;
        }
        if (filePostDto.filePayload.uid) {
          return filePostDto.filePayload.uid == f.filePayload.uid;
        }
        return false;
      });

    return indexToRemove;
  }
}
